// frontend/src/screens/ClientPlaylistScreen.js - VERSION CORRIGÉE AVEC URLS COMPLÈTES
import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useGetPlaylistForClientQuery } from '../slices/playlistsApiSlice';
import { Container, Row, Col, Card, Button, ListGroup, Alert, Spinner, Modal } from 'react-bootstrap';

const ClientPlaylistScreen = () => {
  const { playlistId } = useParams();
  const navigate = useNavigate();
  
  const { 
    data: playlist, 
    isLoading, 
    error,
    refetch 
  } = useGetPlaylistForClientQuery(playlistId);

  const [errorMessage, setErrorMessage] = useState('');
  const [sortedVideos, setSortedVideos] = useState([]);
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [currentVideo, setCurrentVideo] = useState(null);

  useEffect(() => {
    // Mettre à jour le titre de la page
    if (playlist?.name) {
      document.title = `${playlist.name} - Playlist`;
    } else {
      document.title = 'Playlist - Formation';
    }

    // Trier les vidéos par ordre
    if (playlist?.videos) {
      // ✅ CORRECTION : Créer une copie du tableau avant de trier
      const videosCopy = [...playlist.videos];
      const sorted = videosCopy.sort((a, b) => (a.order || 0) - (b.order || 0));
      setSortedVideos(sorted);
    }

    if (error) {
      if (error.status === 403) {
        setErrorMessage('Accès refusé. Vous devez acheter cette formation pour accéder au contenu.');
      } else if (error.status === 404) {
        setErrorMessage('Playlist non trouvée.');
      } else {
        setErrorMessage('Erreur lors du chargement de la playlist.');
      }
    }
  }, [error, playlist]);

  const handleRetry = () => {
    setErrorMessage('');
    refetch();
  };

  const handleGoBack = () => {
    navigate(-1);
  };

  const handleGoToCourses = () => {
    navigate('/mes-cours');
  };

  // ✅ FONCTION CRITIQUE : Obtenir l'URL complète du fichier
  const getFileUrl = (filePath) => {
    if (!filePath) return '#';
    
    // Si le chemin commence déjà par http, le retourner tel quel
    if (filePath.startsWith('http')) {
      return filePath;
    }
    
    // ✅ CORRECTION : Construire l'URL complète vers le backend
    const backendUrl = process.env.NODE_ENV === 'production' 
      ? 'https://api-tn.licorne-academy.com' 
      : 'https://api-tn.licorne-academy.com';
    
    // S'assurer que le chemin commence par /
    const normalizedPath = filePath.startsWith('/') ? filePath : `/${filePath}`;
    return `${backendUrl}${normalizedPath}`;
  };

  // Ouvrir la vidéo dans une modal
  const handleVideoClick = (video) => {
    if (video.fileType === 'video') {
      setCurrentVideo(video);
      setShowVideoModal(true);
    } else {
      // Pour les autres types de fichiers, téléchargement direct
      const fileUrl = getFileUrl(video.path);
      // console.log('📥 Téléchargement fichier:', fileUrl);
      window.open(fileUrl, '_blank');
    }
  };

  const handleCloseVideoModal = () => {
    setShowVideoModal(false);
    setCurrentVideo(null);
  };

  if (isLoading) {
    return (
      <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
        <Spinner animation="border" role="status" variant="primary">
          <span className="visually-hidden">Chargement...</span>
        </Spinner>
      </Container>
    );
  }

  if (errorMessage) {
    return (
      <Container className="my-5">
        <Row className="justify-content-center">
          <Col md={8} lg={6}>
            <Alert variant="danger" className="text-center">
              <Alert.Heading>Erreur</Alert.Heading>
              <p>{errorMessage}</p>
              <hr />
              <div className="d-flex justify-content-center gap-2">
                <Button variant="outline-danger" onClick={handleRetry}>
                  Réessayer
                </Button>
                <Button variant="outline-primary" onClick={handleGoBack}>
                  Retour
                </Button>
                {error?.status === 403 && (
                  <Button variant="primary" onClick={handleGoToCourses}>
                    Voir mes cours
                  </Button>
                )}
              </div>
            </Alert>
          </Col>
        </Row>
      </Container>
    );
  }

  if (!playlist) {
    return (
      <Container className="my-5">
        <Alert variant="warning" className="text-center">
          <p>Aucune donnée de playlist disponible.</p>
          <Button variant="outline-warning" onClick={handleRetry}>
            Réessayer
          </Button>
        </Alert>
      </Container>
    );
  }

  return (
    <>
      <Container className="my-4">
        {/* En-tête de la playlist */}
        <Row className="mb-4">
          <Col>
            <nav aria-label="breadcrumb">
              <ol className="breadcrumb">
                <li className="breadcrumb-item">
                  <Link to="/mes-cours">Mes Cours</Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to={`/course/${playlist.product?._id}/playlists`}>
                    {playlist.product?.name || 'Formation'}
                  </Link>
                </li>
                <li className="breadcrumb-item active" aria-current="page">
                  {playlist.name}
                </li>
              </ol>
            </nav>
            
            <div className="d-flex justify-content-between align-items-start">
              <div>
                <h1 className="h2 mb-2">{playlist.name}</h1>
                {playlist.description && (
                  <p className="text-muted mb-0">{playlist.description}</p>
                )}
              </div>
              <Button variant="outline-secondary" onClick={handleGoBack}>
                Retour
              </Button>
            </div>
          </Col>
        </Row>

        {/* Image de la playlist */}
        {playlist.image && (
          <Row className="mb-4">
            <Col>
              <Card>
                <Card.Img 
                  variant="top" 
                  src={getFileUrl(playlist.image)} 
                  alt={playlist.name}
                  style={{ maxHeight: '300px', objectFit: 'cover' }}
                  onError={(e) => {
                    console.error('❌ Erreur chargement image:', playlist.image);
                    e.target.style.display = 'none';
                  }}
                />
              </Card>
            </Col>
          </Row>
        )}

        {/* Vidéos de la playlist */}
        <Row>
          <Col lg={8}>
            <Card>
              <Card.Header className="bg-primary text-white">
                <h3 className="h5 mb-0">
                  Contenu de la playlist 
                  <span className="badge bg-light text-primary ms-2">
                    {sortedVideos.length} vidéo(s)
                  </span>
                </h3>
              </Card.Header>
              <Card.Body className="p-0">
                {sortedVideos.length > 0 ? (
                  <ListGroup variant="flush">
                    {sortedVideos.map((video, index) => (
                      <ListGroup.Item key={video._id} className="p-3">
                        <div className="d-flex align-items-center">
                          <div className="me-3 text-muted">
                            <strong>{index + 1}.</strong>
                          </div>
                          <div className="flex-grow-1">
                            <h6 className="mb-1">{video.name}</h6>
                            <div className="d-flex text-muted small">
                              <span className="me-3">
                                {video.fileType === 'video' ? '🎥 Vidéo' : '📄 Document'}
                              </span>
                              <span>
                                {video.size && (video.size / (1024 * 1024)).toFixed(2)} MB
                              </span>
                              <span className="ms-3">
                                <small>URL: {getFileUrl(video.path)}</small>
                              </span>
                            </div>
                          </div>
                          <div>
                            <Button 
                              variant="primary" 
                              size="sm"
                              onClick={() => handleVideoClick(video)}
                            >
                              {video.fileType === 'video' ? 'Regarder' : 'Télécharger'}
                            </Button>
                          </div>
                        </div>
                      </ListGroup.Item>
                    ))}
                  </ListGroup>
                ) : (
                  <div className="text-center p-4">
                    <p className="text-muted mb-0">Aucune vidéo disponible dans cette playlist.</p>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>

          {/* Informations supplémentaires */}
          <Col lg={4}>
            <Card>
              <Card.Header>
                <h4 className="h6 mb-0">Informations</h4>
              </Card.Header>
              <Card.Body>
                <div className="mb-3">
                  <strong>Formation:</strong>
                  <br />
                  <span className="text-muted">
                    {playlist.product?.name || 'Non spécifiée'}
                  </span>
                </div>
                
                {playlist.createdAt && (
                  <div className="mb-3">
                    <strong>Créée le:</strong>
                    <br />
                    <span className="text-muted">
                      {new Date(playlist.createdAt).toLocaleDateString('fr-FR')}
                    </span>
                  </div>
                )}

                <div>
                  <strong>Total des vidéos:</strong>
                  <br />
                  <span className="text-muted">
                    {sortedVideos.length} élément(s)
                  </span>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>

      {/* Modal pour lire les vidéos */}
      <Modal show={showVideoModal} onHide={handleCloseVideoModal} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>{currentVideo?.name}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {currentVideo && (
            <div className="text-center">
              <video 
                controls 
                autoPlay 
                style={{ width: '100%', maxHeight: '70vh' }}
                onError={(e) => {
                  console.error('❌ Erreur lecture vidéo:', getFileUrl(currentVideo.path));
                  alert('Erreur lors du chargement de la vidéo. Vérifiez la console pour plus de détails.');
                }}
              >
                <source src={getFileUrl(currentVideo.path)} type={currentVideo.mimetype} />
                Votre navigateur ne supporte pas la lecture de vidéos.
              </video>
              <div className="mt-2">
                <small className="text-muted">
                  URL: {getFileUrl(currentVideo.path)}
                </small>
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseVideoModal}>
            Fermer
          </Button>
          <Button 
            variant="outline-primary" 
            onClick={() => window.open(getFileUrl(currentVideo?.path), '_blank')}
          >
            Ouvrir dans un nouvel onglet
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default ClientPlaylistScreen;